/**
 * @file bmlCommon/pro/container
 * @description 详情页布局
 * @author wangting31
 */

import React from 'react';
import classNames from 'classnames';
import {Tabs} from 'acud';
import type {TabsType} from 'acud/lib/tabs/tabs';

import BottomSubmit from '../../BottomSubmit';
import PageTitle, {PageTitleBtnProps, PageTitleProps} from '../pageTitle';
import PageLoading, {LoadingProps} from '../../../common/Loading';
import EmptyStatus, {EmptyStatusProps} from '../../EmptyStatus';
import {ACUD_PRO_PREFIX} from '../../../_common/constants';

import './index.less';

type PageTitleRenderType = (props: any) => string;

type OnChangeMD = (activeKey: string) => void;

type TitleProps = PageTitleProps & {
    title?: React.ReactNode | string;
    backUrl?: string;
    backText?: string;
    btnConfig?: PageTitleBtnProps[];
    custom?: React.ReactNode;
    className?: string;
    pageTitleRender?: PageTitleRenderType;
}

interface TabProps {
    defaultActiveKey?: string;
    activeKey?: string;
    className?: string;
    type?: TabsType;
    // 切换面板的回调
    // 切换Tab一般情况都是切换路由，给定切换逻辑，其他逻辑在change里面实现？
    onChange?: OnChangeMD;
    // xxxx/ProLayoutActiveKey/xxxx
    tabRouter?: string;
    // TODO
    history?: any;
}

interface TabListProps {
    key: string;
    tab: React.ReactNode | string;
    content: React.ReactNode;
    basicInfo?: boolean;
    valid?: boolean;
    ready?: boolean;
    visible?: boolean;
}
interface BtnSubmitProps {
    type?: string;
    size?: string;
    text?: React.ReactNode | string;
    loading?: boolean;
    onClick?: () => void;
}
export type ProContainerProps = TitleProps & {
    // loading
    className?: string;
    titleClassName?: string;
    prefixCls?: string;
    loading?: boolean;
    loadingProps?: LoadingProps;
    // error
    emptyStatusProps?: EmptyStatusProps;
    // tabs
    tabList?: TabListProps;
    tabInfo?: TabProps;
    btnSubmitOptions?: BtnSubmitProps[];
    extra?: React.ReactNode | string;
};

const titleRander = (props: TitleProps): React.ReactNode => {
    const {
        backUrl = '/',
        backText,
        title = '',
        btnConfig,
        custom,
        className = '',
        pageTitleRender,
        extra,
        ...rest} = props;
    if (pageTitleRender && typeof pageTitleRender === 'function') {
        return pageTitleRender(props);
    }

    return (
        <PageTitle
            className={className}
            backText={backText}
            title={title}
            btnConfig={btnConfig}
            custom={custom}
            backUrl={backUrl}
            extra={extra}
            {...rest}
        />
    );
};

const ProContainer: React.FC<ProContainerProps> = props => {
    const {
        prefixCls: customizePrefixCls = `${ACUD_PRO_PREFIX}-page-layout-container`,
        backUrl = '/',
        backText,
        title,
        btnConfig,
        custom,
        className = '',
        titleClassName = '',
        extra,
        pageTitleRender,
        tabList,
        tabInfo,
        children,
        loading,
        loadingProps,
        btnSubmitOptions,
        emptyStatusProps
    } = props;

    const containerClass = classNames(className, customizePrefixCls);

    if (emptyStatusProps) {
        return <EmptyStatus {...emptyStatusProps} />;
    }

    let contentDom = <div className="content-area">{children}</div>;

    const titleDom = titleRander({
        backUrl,
        backText,
        title,
        btnConfig,
        custom,
        extra,
        pageTitleRender,
        className: titleClassName
    });

    if (Array.isArray(tabList) && tabInfo) {
        const {tabRouter, onChange, history} = tabInfo;
        const tabsProps = {
            ...tabInfo
        };
        if (tabRouter) {
            tabsProps.onChange = activeKey => {
                history?.push(tabRouter.replace('ProContainerActiveKey', activeKey));
                if (typeof onChange === 'function') {
                    onChange(activeKey);
                }
            };
            delete tabsProps.tabRouter;
            delete tabsProps.history;
        }

        contentDom = (
            <Tabs {...tabsProps} destroyInactiveTabPane>
                {tabList
                    .filter(item => ('visible' in item ? item.visible : true))
                    .map(item => (
                        <Tabs.TabPane
                            tab={item.tab}
                            key={item.key}
                            className="card-content-area"
                            disabled={'valid' in item && !item.valid}
                        >
                            <div className={!item.basicInfo ? 'content-detail' : ''}>
                                {!('ready' in item) || item.ready ? item.content : null}
                            </div>
                        </Tabs.TabPane>
                    ))}
            </Tabs>
        );
    }
    return (
        <div className={containerClass}>
            <div className="header-area">{titleDom}</div>
            {loading ? <PageLoading {...loadingProps} /> : contentDom}
            {btnSubmitOptions && <BottomSubmit options={btnSubmitOptions} />}
        </div>
    );
};

export {ProContainer};
